// src/utils/lazyWithRetry.js
// React.lazy, hardened for bad networks. Two failure modes matter here:
//
// 1. A chunk request drops mid-flight (common on 3G). Plain React.lazy throws
//    and the route dies. We retry with backoff first.
// 2. A new deploy replaces the chunk files while a tab is still open on the old
//    build, so the hashed filename 404s forever. Retrying can't fix that — a
//    one-time reload onto the new build can. We guard with sessionStorage so a
//    genuine failure can never become a reload loop.
import { lazy } from 'react';

const RELOAD_KEY = 'tt_chunk_reload';

export default function lazyWithRetry(factory, { retries = 2, delay = 600 } = {}) {
  return lazy(async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        const mod = await factory();
        sessionStorage.removeItem(RELOAD_KEY); // recovered — clear the guard
        return mod;
      } catch (err) {
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, delay * (attempt + 1)));
          continue;
        }
        // Out of retries: likely a stale build. Reload once, then give up so
        // the ErrorBoundary can show a real message instead of looping.
        let alreadyReloaded = false;
        try { alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === '1'; } catch {}
        if (!alreadyReloaded) {
          try { sessionStorage.setItem(RELOAD_KEY, '1'); } catch {}
          window.location.reload();
          // Never resolves — the reload takes over.
          return new Promise(() => {});
        }
        throw err;
      }
    }
  });
}
